import React from "react";
import Reveal from "./ui/Reveal";
import SectionLabel from "./ui/SectionLabel";
import SpotlightCard from "./ui/SpotlightCard";
import MagneticButton from "./ui/MagneticButton";

const WHATSAPP = (msg) =>
  `https://wa.me/5516997502577?text=${encodeURIComponent(msg)}`;

const Check = ({ className }) => (
  <svg
    className={`mt-0.5 h-5 w-5 shrink-0 ${className}`}
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const PLANS = [
  {
    id: "basico",
    name: "Básico",
    tagline: "Para quem está começando.",
    price: "R$ 150",
    period: "/mês",
    features: [
      "Gestão de Clientes e Pets",
      "Agendamento Simples e Recorrente",
      "Gestão de Estoque",
      "Fluxo de Caixa",
      "Treinamento de Equipe",
    ],
    cta: "Solicite Visita Técnica",
    href: WHATSAPP(
      "Olá! Gostaria de solicitar uma visita técnica para conhecer o plano Básico do PawSuite."
    ),
    featured: false,
  },
  {
    id: "pro",
    name: "Profissional",
    tagline: "Tudo para crescer seu pet shop.",
    price: "Em breve",
    period: "",
    features: [
      "Tudo do plano Básico",
      "Portal do Tutor Exclusivo",
      "Fluxo de Caixa Premium",
      "Controle Clínico Veterinário",
      "Suporte Prioritário 24/7",
      "Treinamento de Equipe",
    ],
    cta: "Quero ser avisado",
    href: WHATSAPP(
      "Olá! Quero ser avisado quando o plano Profissional do PawSuite for lançado."
    ),
    featured: true,
  },
  {
    id: "custom",
    name: "Customizado",
    tagline: "Trabalhe do seu jeito.",
    price: "Sob consulta",
    period: "",
    features: [
      "Você define as funcionalidades",
      "Integrações sob medida",
      "Acompanhamento dedicado",
    ],
    cta: "Falar com Consultor",
    href: WHATSAPP(
      "Olá! Gostaria de conversar sobre um plano customizado do PawSuite."
    ),
    featured: false,
  },
];

const PlanCard = ({ plan, index }) => {
  if (plan.featured) {
    return (
      <Reveal delay={index * 130} variant="scale" className="lg:-mt-6">
        {/* Casca com borda em gradiente animado */}
        <div className="relative rounded-[34px] p-[1.5px]">
          <div className="absolute inset-0 rounded-[34px] bg-gradient-to-br from-tealVitality via-pawOrange to-tealVitality opacity-90" />
          <div className="absolute -inset-3 -z-10 rounded-[42px] bg-gradient-to-br from-tealVitality/30 to-pawOrange/30 blur-2xl" />

          <SpotlightCard
            spot="rgba(255,255,255,.09)"
            line="rgba(255,255,255,.45)"
            className="relative flex h-full flex-col overflow-hidden rounded-[33px] bg-midnight p-8 lg:p-10"
          >
            <span className="absolute right-0 top-0 rounded-bl-2xl bg-gradient-to-r from-pawOrange to-amberGlow px-4 py-2 text-[11px] font-extrabold uppercase tracking-widest text-midnight">
              Mais popular
            </span>

            <h3 className="font-display text-xl font-bold text-white">
              {plan.name}
            </h3>
            <p className="mb-7 mt-1.5 text-sm text-white/50">{plan.tagline}</p>

            <div className="mb-8 flex items-baseline gap-1.5">
              <span className="font-display text-4xl font-extrabold text-white">
                {plan.price}
              </span>
            </div>

            <ul className="mb-9 flex-1 space-y-3.5">
              {plan.features.map((feature) => (
                <li
                  key={feature}
                  className="flex items-start gap-3 text-[15px] text-white/70"
                >
                  <Check className="text-pawOrange" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <MagneticButton
              href={plan.href}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full rounded-2xl bg-pawOrange py-4 font-bold text-midnight shadow-amber transition-all duration-300 hover:bg-amberGlow"
            >
              {plan.cta}
            </MagneticButton>
          </SpotlightCard>
        </div>
      </Reveal>
    );
  }

  return (
    <Reveal delay={index * 130}>
      <SpotlightCard
        spot="rgba(0,178,169,.12)"
        line="rgba(0,178,169,.6)"
        className="flex h-full flex-col rounded-[33px] border border-midnight/[.07] bg-white p-8 shadow-soft transition-all duration-500 hover:-translate-y-1.5 hover:shadow-lift lg:p-10"
      >
        <h3 className="font-display text-xl font-bold text-midnight">
          {plan.name}
        </h3>
        <p className="mb-7 mt-1.5 text-sm text-midnight/50">{plan.tagline}</p>

        <div className="mb-8 flex items-baseline gap-1">
          <span className="font-display text-4xl font-extrabold text-midnight">
            {plan.price}
          </span>
          {plan.period && (
            <span className="text-midnight/45">{plan.period}</span>
          )}
        </div>

        <ul className="mb-9 flex-1 space-y-3.5">
          {plan.features.map((feature) => (
            <li
              key={feature}
              className="flex items-start gap-3 text-[15px] text-midnight/65"
            >
              <Check className="text-tealVitality" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <MagneticButton
          href={plan.href}
          target="_blank"
          rel="noopener noreferrer"
          strength={0.18}
          className="w-full rounded-2xl border-2 border-midnight py-4 font-bold text-midnight transition-colors duration-300 hover:bg-midnight hover:text-white"
        >
          {plan.cta}
        </MagneticButton>
      </SpotlightCard>
    </Reveal>
  );
};

const Pricing = () => (
  <section id="pricing" className="relative overflow-hidden bg-softIvory py-28">
    <div className="pointer-events-none absolute left-1/2 top-20 h-[500px] w-[900px] -translate-x-1/2 rounded-full bg-pawOrange/[.07] blur-[130px]" />

    <div className="relative mx-auto max-w-7xl px-6">
      <div className="mx-auto mb-20 max-w-3xl text-center">
        <Reveal>
          <SectionLabel tone="teal">Planos e Preços</SectionLabel>
        </Reveal>
        <Reveal delay={90}>
          <h2 className="mb-6 mt-6 font-display text-[clamp(2rem,4.4vw,3.25rem)] font-extrabold leading-[1.08] text-midnight">
            Um plano que cresce
            <br className="hidden sm:block" /> junto com você
          </h2>
        </Reveal>
        <Reveal delay={170}>
          <p className="text-lg leading-relaxed text-midnight/55">
            Sem taxa de instalação, sem letra miúda. Cancele a qualquer momento.
          </p>
        </Reveal>
      </div>

      <div className="grid items-stretch gap-7 md:grid-cols-2 lg:grid-cols-3">
        {PLANS.map((plan, i) => (
          <PlanCard key={plan.id} plan={plan} index={i} />
        ))}
      </div>

      <Reveal delay={400}>
        <p className="mt-12 text-center text-sm text-midnight/40">
          Ainda em dúvida?{" "}
          <a
            href="#faq"
            className="font-semibold text-tealVitality underline decoration-tealVitality/30 underline-offset-4 transition-colors hover:decoration-tealVitality"
          >
            Veja as perguntas frequentes
          </a>
        </p>
      </Reveal>
    </div>
  </section>
);

export default Pricing;
